import {
  Children, Fragment, cloneElement, createContext, isValidElement, useContext, useEffect, useId,
  useRef, useState, type MouseEvent, type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Link, useRouter, type LinkProps } from "@tanstack/react-router";
import { useT } from "../i18n";
import { Erklaerung } from "../bausteine";
import { Zeichen } from "../bausteine/Zeichen";
import { useGeraet } from "./geraet";
import { vorigerWeg, zurueckGehen } from "./verlauf";

/* ══════════════════════════════════════════════════════════════════
   Der Kopf gehört der Maske.

   Links das Logo in der Breite der Seitenleiste, rechts davon eine
   durchgehende Leiste, die ganz der Maske gehört: ihr Name zuerst, dann
   der Zusammenhang, dann ihre Steuerung und ihre Knöpfe
   (28-designkonzept.md, „Der Kopf gehört der Maske").

   Wer tiefer steht als eine Liste, bekommt davor den Rückweg — links,
   als Pfeil, **vor dem Namen in derselben Zeile** (`Zurueck`). Rechts
   stehen nur Handlungen.

   Der Prototyp hat den fertigen `.kopf` mit `appendChild` aus dem
   Schirm in die Hauskopfzeile geschoben. Das geht in React nicht — der
   Baum gehört React, nicht uns. Ein Portal macht dasselbe, nur richtig
   herum: Die Maske schreibt ihren Kopf, wo er inhaltlich hingehört, und
   er erscheint, wo er hingehört.

   Der Benutzername steht dort **nicht**. Er steht unten links, weil man
   ihn einmal am Tag liest — die obere Leiste ist zu wertvoll dafür.
   ══════════════════════════════════════════════════════════════════ */

const Ziel = createContext<HTMLElement | null>(null);

export function KopfZiel({ children }: { children: ReactNode }) {
  return <div id="kopfziel" className="kopfziel">{children}</div>;
}

export function KopfRaum({ ziel, children }: { ziel: HTMLElement | null; children: ReactNode }) {
  return <Ziel.Provider value={ziel}>{children}</Ziel.Provider>;
}

/**
 * Der Kopf einer Maske. `brett` schaltet auf die dichtere Form, die das
 * Brett braucht — mehr Steuerelemente, weniger Luft.
 */
export function Kopf({ brett, children }: { brett?: boolean; children: ReactNode }) {
  const ziel = useContext(Ziel);
  /* **Geordnet wird am Finger, nicht nur am Telefon** (17.09.2026, abends).
     Das Tablet bekam bis dahin den Schreibtischkopf, und der brach dort um:
     der Auftrag 311 px in sechs Reihen, das Angebot 230, die Planung 225.
     Drei Masken haben sich daraufhin ihren Kopf selbst geordnet — der
     Auftrag und das Angebot mit einem eigenen „⋯" (`Aktionsmenue.tsx`),
     die Ressourcen und die Abrechnung, indem sie ihren Umschalter in die
     Filterzeile schoben. Dreimal dasselbe Bild, dreimal eigener Code. */
  const geordnet = useGeraet() !== "schreibtisch";
  // Beim ersten Bild steht das Ziel noch nicht; ein Zustandswechsel holt
  // das Portal nach, sobald es da ist.
  const [bereit, setzeBereit] = useState(false);
  useEffect(() => setzeBereit(true), []);

  const inhalt = brett
    ? <div className="brett-kopf">{children}</div>
    : geordnet
      ? <KopfGeordnet>{children}</KopfGeordnet>
      : <div className="kopf">{children}</div>;
  if (!ziel || !bereit) return null;
  return createPortal(inhalt, ziel);
}

/* ══════════════ Der geordnete Kopf (17.09.2026) ══════════════

   Luca: *„komplett mobile-fähig … ein komplettes mobiles Interface, welches
   vollumfänglich funktioniert."* Und der Grundsatz dazu aus 09-mobile.md:
   **nicht schrumpfen, sondern umbauen.**

   Am Schreibtisch ist der Kopf eine Zeile: Rückweg, Name, Zusammenhang,
   Luft, Knöpfe. Auf 390 px brach dieselbe Zeile um — **gemessen:** der
   Auftrag 295 px hoch in sechs Reihen (Pfeil, Name in zwei Zeilen,
   Nummer mit Marke und „Status", drei Knöpfe, „Stornieren" allein), das
   Angebot 218 px, die Ressourcen 203. Der Rollfläche des Auftrags blieben
   darunter 333 px.

   **Am Finger ordnet der Kopf, was die Maske hineinschreibt**, statt es
   umbrechen zu lassen — die Maske schreibt dafür nichts anders:

   1. **Oben eine Zeile:** Rückweg und Name (höchstens zwei Zeilen).
   2. **Darunter der Zusammenhang** — Nummer, Marke, Umschalter —, in einer
      Reihe, die zur Seite rollt, statt umzubrechen; daneben **eine**
      Hauptaktion und der Knopf für den Rest.
   3. **Alles Weitere steht im Menü** hinter „⋯".

   **Was wohin gehört, steht schon im Kopf der Maske:** Was vor `<Luft />`
   steht, ist Zusammenhang; was dahinter steht, sind Handlungen
   (28-designkonzept.md, „Der Kopf gehört der Maske"). Die Hauptaktion ist
   der gelbe Knopf (`art="haupt"`); gibt es keinen, der Knopf, der nur für
   schmale Schirme da ist (`nur-schmal` — „Status" im Auftrag); und gibt es
   nur eine Handlung, steht sie selbst da.

   **Das Menü bleibt gebaut, wenn es zu ist.** Ein Knopf darin kann sein
   eigenes Blatt tragen — „Texte" öffnet sein Deckblatt aus sich heraus
   (`bausteine/Belegtexte.tsx`). Nähme das Schließen des Menüs den Knopf
   aus dem Baum, ginge das Blatt mit ihm zu, kaum dass es offen ist. */

/** Die Kinder des Kopfs ohne Fragmente — mit Schlüsseln, die ein Bild überdauern. */
function flach(kinder: ReactNode, vorsatz = ""): ReactNode[] {
  const aus: ReactNode[] = [];
  Children.toArray(kinder).forEach((k) => {
    if (isValidElement<{ children?: ReactNode }>(k) && k.type === Fragment) {
      aus.push(...flach(k.props.children, `${vorsatz}${String(k.key)}/`));
    } else if (isValidElement(k)) {
      aus.push(cloneElement(k, { key: `${vorsatz}${String(k.key)}` }));
    } else {
      aus.push(k);
    }
  });
  return aus;
}

/** Trägt dieses Kind eine der Klassen? `Knopf` hängt `art` als Klasse an. */
function hatKlasse(k: ReactNode, klasse: string): boolean {
  if (!isValidElement<{ art?: string; className?: string }>(k)) return false;
  return k.props.art === klasse || (k.props.className ?? "").split(/\s+/).includes(klasse);
}

/* **Wo die Zeilen stehen, entscheidet der Stil** (`stile/telefon.css`): Am
   Telefon hochkant sind es zwei Reihen (Breite ist knapp, Höhe nicht), quer
   und auf dem Tablet eine (dort ist es umgekehrt). Der Baum ist derselbe.

   ── Drei Blöcke seit dem 18.09.2026, nicht zwei ────────────────────

   Bis dahin standen Rückweg, Name **und** die Handlungen in einer Zeile und
   der Zusammenhang darunter. Am Telefon hochkant teilte sich der Name die
   Zeile damit mit allem, was Fläche braucht. Nachgerechnet auf 390 px im
   Auftrag: 108 px Werkzeugspalte der Schale, 24 px Rand, 44 px Rückweg,
   44 px „⋯", dazu zwei Abstände — dem **Kundennamen blieben 150 px**, und
   „Musterbau Hoch- und Tiefbau GmbH" endete nach zwei Zeilen als „Musterbau
   Hoch- un…". Luca, 18.09.2026: *„Wo musst du im Header … die Größen
   technisch ein wenig anders machen, sodass man den Kunden sauber lesen
   kann?"*

   **Die Handlungen stehen deshalb in einem eigenen Block.** Am Telefon
   hochkant rücken sie neben den Zusammenhang in die zweite Reihe: Der Name
   bekommt die erste Reihe für sich (204 statt 150 px), und die Reihe
   darunter trägt zusammen, was ohnehin Griffe sind — Nummer und Marke
   links, Hauptaktion und „⋯" rechts. Die Höhe bleibt dieselbe, weil es
   vorher wie nachher zwei Reihen sind.

   **Quer und auf dem Tablet ändert sich nichts.** Dort geben alle drei
   Blöcke ihre Kinder an die eine Reihe ab (`display: contents`), und die
   Reihenfolge ist die des Baums: Rückweg, Name, Zusammenhang, Handlungen
   rechts. Der Platzhalter `kopf-luft` ist dafür entfallen — `margin-left:
   auto` am Handlungsblock tut dasselbe und zählt beim Umbruch nicht mit.

   **Das gilt für jede Maske, die diesen Kopf benutzt** — Auftrag, Angebot,
   Kundenakte, Stammdaten. Keine Maske schreibt dafür etwas anders. */
function KopfGeordnet({ children }: { children: ReactNode }) {
  const vorn: ReactNode[] = [];
  const zusammenhang: ReactNode[] = [];
  const handlungen: ReactNode[] = [];
  let nachLuft = false;
  for (const k of flach(children)) {
    const typ = isValidElement(k) ? k.type : null;
    if (typ === Zurueck || typ === ZurueckKnopf || typ === Titel) vorn.push(k);
    // Das Fragezeichen erklärt den Namen und bleibt bei ihm.
    else if (typ === Erklaerung && !nachLuft) vorn.push(k);
    // Der Trennstrich trennt Name und Zusammenhang in EINER Zeile; stehen
    // sie untereinander, trennt die Zeile.
    else if (typ === KopfTrenn) continue;
    else if (typ === Luft) nachLuft = true;
    else if (nachLuft) handlungen.push(k);
    else zusammenhang.push(k);
  }

  const haupt = handlungen.find((k) => hatKlasse(k, "haupt"))
    ?? handlungen.find((k) => hatKlasse(k, "nur-schmal"))
    ?? (handlungen.length === 1 ? handlungen[0] : undefined);
  const rest = handlungen.filter((k) => k !== haupt);

  return (
    <div className="kopf kopf-geordnet">
      <div className="kopf-zeile">{vorn}</div>
      {zusammenhang.length ? <div className="kopf-zusammenhang">{zusammenhang}</div> : null}
      {/* Leer wird der Block nicht gezeichnet: Eine leere Spalte im Raster
          schöbe den Zusammenhang von der Kante weg. */}
      {haupt || rest.length ? (
        <div className="kopf-handlungen">
          {haupt}
          {rest.length ? <KopfMenue>{rest}</KopfMenue> : null}
        </div>
      ) : null}
    </div>
  );
}

/** Der Knopf „⋯" und das Menü darunter. Zu ist es verborgen, nicht abgebaut.
 *  Seit Signal (07.10.2026) auch am Schreibtisch: Die Akte stellt dort ihre
 *  selteneren Handlungen selbst dahinter (docs/64, „höchstens zwei
 *  beschriftete Handlungen im Kopf“). */
export function KopfMenue({ children }: { children: ReactNode }) {
  const t = useT();
  const id = useId();
  const [offen, setzeOffen] = useState(false);
  const raum = useRef<HTMLDivElement>(null);

  // Daneben tippen oder `Esc` schließt — ein Menü, das stehen bleibt, wenn
  // man woanders hingreift, liegt beim nächsten Griff im Weg.
  useEffect(() => {
    if (!offen) return;
    const daneben = (e: PointerEvent) => {
      if (!raum.current?.contains(e.target as Node)) setzeOffen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setzeOffen(false); };
    document.addEventListener("pointerdown", daneben);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", daneben);
      document.removeEventListener("keydown", esc);
    };
  }, [offen]);

  return (
    <div className="kopf-mehr" ref={raum}>
      <button
        type="button"
        className="knopf kopf-mehr-knopf"
        aria-expanded={offen}
        aria-controls={id}
        aria-label={t("kopf.mehr")}
        title={t("kopf.mehr")}
        onClick={() => setzeOffen((x) => !x)}
      >
        <Zeichen name="mehr" />
      </button>
      {/* Wer darin etwas wählt, ist fertig mit dem Menü. Gefragt wird am
          Ziel: Ein Klick auf die Fläche zwischen zwei Knöpfen schließt nicht. */}
      <div
        className="kopf-menue"
        id={id}
        hidden={!offen}
        onClick={(e) => { if ((e.target as HTMLElement).closest("button, a")) setzeOffen(false); }}
      >
        {children}
      </div>
    </div>
  );
}

/** Der Titel der Maske — derselbe Name wie in der Navigation, immer zuerst. */
export function Titel({ children }: { children: ReactNode }) {
  return <h2 className="titel">{children}</h2>;
}

/** Der senkrechte Strich, der den Namen vom Zusammenhang trennt. */
export function KopfTrenn() {
  return <span className="kopf-trenn" />;
}

/** Der Platzhalter, der alles Weitere nach rechts drückt. */
export function Luft() {
  return <div className="luft" />;
}

/** Ein Weg ohne Platzhalter: Der Rückweg führt zu einer Liste, und eine
 *  Liste braucht keine Nummer. `/kunden/$nr` ohne `nr` fällt so schon
 *  beim Übersetzen auf, nicht erst als „Seite nicht gefunden". */
type Listenweg = Exclude<NonNullable<LinkProps["to"]>, `${string}$${string}` | "." | "..">;

/**
 * Der Rückweg — ein Pfeil vor dem Namen, in derselben Zeile.
 *
 * Er ersetzte am 15.09.2026 die Krume „Aufträge → V-2026-0030" und den
 * Knopf „Zurück" rechts. Man kommt von links und geht nach links; rechts
 * stehen Handlungen, die etwas tun (28-designkonzept.md, „Der Weg zurück
 * steht links").
 *
 * **Am 16.09.2026 ist er eine Zeile nach unten gewandert.** Vorher stand
 * er klein über dem Titel, zusammen mit dem Namen der Liste. Luca beim
 * Durchgehen: *„Die Art und Weise ist nicht so schnell sichtbar für den
 * Sachbearbeiter."* Und das stimmt: Über dem Titel steht der Weg in einer
 * eigenen, halb so hohen Zeile in Zweitrang — beim Überfliegen einer
 * Maske sucht das Auge dort nichts. Jetzt steht er **vor dem Namen**, in
 * derselben Zeile, senkrecht mittig und genau so hoch wie die Knöpfe
 * daneben (32 px). Damit liegt er auf der Linie, die man ohnehin liest.
 *
 * **Der Name der Liste steht nicht mehr daneben.** Ein Pfeil neben einem
 * 19 px großen Kundennamen und dazu noch das Wort „Aufträge" macht drei
 * Dinge in einer Zeile, von denen zwei dasselbe sagen. Das Ziel steht im
 * `aria-label` und im `title` — gesprochen und beim Verweilen, nicht
 * ständig.
 *
 * Ein Verweis, kein Knopf: Er wechselt die Maske, er tut nichts. Damit
 * geht auch Mittelklick in einen neuen Tab, und die Liste wird beim
 * Überfahren schon geholt (`defaultPreload: "intent"`).
 *
 * Das Ziel steht fest und ist nicht „die Seite davor": Wer aus der
 * Planung in einen Auftrag springt, soll unter „Aufträge" auch die
 * Aufträge bekommen. `Esc` ist nicht belegt; es gehört den Deckblättern,
 * Menüs und Wählern.
 *
 * **Wie er dorthin geht, entscheidet der Verlauf** (17.09.2026,
 * 40-mobiles-grundgeruest.md, Schritt 7). Bis dahin legte jeder Druck einen
 * neuen Eintrag an, und die Zurückgeste auf Android führte danach wieder ins
 * Detail — eine Schleife aus Liste und Detail. Jetzt: Steht die Liste direkt
 * davor, geht der Pfeil im Verlauf zurück (mit ihren Filtern und ihrem
 * Rollstand); sonst **ersetzt** er das Detail durch die Liste
 * (`schale/verlauf.ts`). Mittelklick und Strg-Klick öffnen weiter einen
 * neuen Reiter — dafür bleibt es ein Verweis.
 *
 * Der Aufbau im Kopf — drei Geschwister, kein Kasten darum:
 *
 *     <Kopf>
 *       <Zurueck zu="/auftraege" wort="nav.auftraege" />
 *       <Titel>{kunde}</Titel>
 *       <KopfTrenn />
 *       <span className="kopf-wann">{nr}</span>
 *       …
 */
export function Zurueck({ zu, wort, satz }: {
  zu: Listenweg;
  /** Der Name des Ziels, wie die Navigation ihn nennt — `nav.auftraege`. */
  wort: string;
  /** Was ein Vorleser sagt. Ohne Angabe: „Zurück zu {Ziel}". */
  satz?: string;
}) {
  const t = useT();
  const router = useRouter();
  const ziel = t(wort);
  const beschriftung = satz ? t(satz) : t("kopf.zurueckZu", { ziel });
  const gehen = (e: MouseEvent<HTMLAnchorElement>) => {
    // Was einen neuen Reiter meint, bleibt dem Verweis.
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    if (vorigerWeg() === zu) zurueckGehen();
    else void router.navigate({ to: zu, replace: true });
  };
  return (
    /* `exact`: Ohne das hält der Router `/kunden` unter `/kunden/K-10008`
       für die aktuelle Seite und setzt `aria-current="page"` — ein
       Vorleser sagte dann „aktuelle Seite" über dem Weg hinaus. */
    <Link
      to={zu}
      onClick={gehen}
      activeOptions={{ exact: true }}
      className="zurueck-weg"
      aria-label={beschriftung}
      title={beschriftung}
    >
      <PfeilLinks />
    </Link>
  );
}

/**
 * Derselbe Rückweg als Knopf.
 *
 * Für Blätter, die sich schließen, statt die Maske zu wechseln — das
 * Pflegeblatt der Stammdaten. Gleiche Fläche, gleiche Stelle: Wer den
 * Pfeil im Auftrag gelernt hat, sucht ihn im Maschinenblatt an derselben
 * Kante. Ein „Schließen" rechts daneben wäre derselbe Weg ein zweites
 * Mal; der Ausstieg steht links, Handlungen stehen rechts.
 */
export function ZurueckKnopf({ zu, satz }: { zu: () => void; satz: string }) {
  const t = useT();
  const beschriftung = t(satz);
  return (
    <button
      type="button"
      className="zurueck-weg"
      onClick={zu}
      aria-label={beschriftung}
      title={beschriftung}
    >
      <PfeilLinks />
    </button>
  );
}

/**
 * Der Pfeil nach links. Gezeichnet, nicht getippt: Ein „←" aus der Schrift
 * sitzt in jeder Schrift anders auf der Linie, und im Sprachtext hat ein
 * Pfeil nichts verloren — ein Vorleser sagt dann „Pfeil nach links".
 */
export function PfeilLinks() {
  return (
    <svg
      className="zurueck-pfeil"
      width="14"
      height="14"
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M13.5 8h-11M7 3.5 2.5 8 7 12.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
